import { useComponents } from 'src/store/modules/App/component'
import type { Component, StoryContent, FieldTypeSchema } from '@/types'
import type {
  ComponentMetadata,
  FigmaComponentMetadata,
} from '@/types/components'
import arrayUtils from '@utils/arrayUtils.js'
import { humanize } from '@utils/formats/index'
import { isArray, hasObjectConstructor } from '@/utils/checkers/validation'

/**
 * @param  {String} componentName
 * @param  {String} fieldKey
 * @return {String}
 */
export const getSanitizedComponentFieldName = (
  fieldKey: string,
  componentName?: Component['name'],
): string => {
  if (!componentName) {
    return humanize(fieldKey)
  }

  const componentStore = useComponents()

  const componentSchema = componentStore.components.find(
    (component) => component.name === componentName,
  )

  const schema = componentSchema?.schema

  if (schema && componentSchema && fieldKey in schema) {
    return humanizeComponentName(componentSchema.schema[fieldKey], fieldKey)
  }

  return humanize(fieldKey)
}

/**
 * @param  {String} name
 * @return {String}
 */
export const getSanitizedComponentName = (name: string): string => {
  const componentStore = useComponents()
  const componentSchema = componentStore.components.find(
    (component) => component.name === name,
  )

  return humanizeComponentName(componentSchema, name)
}

/**
 * @param  {Component} componentSchema
 * @param  {String} name
 * @return {String}
 */
export const humanizeComponentName = (
  componentSchema: Component | FieldTypeSchema | undefined,
  name: string,
): string => {
  if (componentSchema && componentSchema.display_name) {
    return componentSchema.display_name
  }

  return humanize(name)
}

export const renameComponent = (
  storyContent: StoryContent,
  oldName: string,
  newName: string,
): StoryContent => {
  if (oldName === newName) {
    return storyContent
  }

  // this clear the story content cache used into the breadcrumbs of the block library
  arrayUtils.clearCache()

  const newStoryContent = arrayUtils.clone(storyContent)

  function justRename(
    json: StoryContent | StoryContent[],
    oldComponentName: string,
    newComponentName: string,
  ): void {
    if (hasObjectConstructor(json)) {
      if (json.component === oldComponentName) {
        json.component = newComponentName
      }

      Object.keys(json).forEach((key) => {
        // @ts-expect-error - need a deep type check here
        if (hasObjectConstructor(json[key]) || isArray(json[key])) {
          // @ts-expect-error - need a deep type check here
          justRename(json[key], oldComponentName, newComponentName)
        }
      })
    }

    if (isArray(json)) {
      json.forEach((item) => {
        if (hasObjectConstructor(item) || isArray(item)) {
          justRename(item, oldComponentName, newComponentName)
        }
      })
    }
  }

  justRename(newStoryContent, oldName, newName)

  return newStoryContent
}

export const findComponentMetadata = (
  component: Component,
  appSlug: string,
): ComponentMetadata | undefined => {
  if (!('metadata' in component) || !Array.isArray(component.metadata)) {
    return
  }

  return component.metadata.find((metadata) => metadata.app_slug === appSlug)
}

export const findFigmaComponentMetadata = (
  component: Component,
): FigmaComponentMetadata | undefined =>
  findComponentMetadata(component, 'figma_to_storyblok')
